import React, { useEffect, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandshake,
  faScaleBalanced,
  faSliders,
  faUsers,
  faCircleInfo,
  faCheck,
  faPaperPlane,
  faClock,
} from "@fortawesome/free-solid-svg-icons";
import { faDiscord } from "@fortawesome/free-brands-svg-icons";
import SubPartnerFees from "./SubPartnerFees";
import { WALLETLESS_URL } from "../utils/partnerApi";

// Revenue Share Models: explains the default (no revenue share) and the
// activatable models. Activate = a request lands in the Instaswap admin CMS
// for manual acceptance; Discord is the fast lane for questions/negotiation.
const TEXT = "#e6eae7";
const BODY = "#a7b2ab";
const MUTED = "#7c847e";
const DISCORD_URL = "https://discord.gg/instaswap";

const MODELS = [
  {
    key: "default",
    icon: faHandshake,
    color: "#7c847e",
    title: "No revenue share (default)",
    tag: "DEFAULT",
    activatable: false,
    points: [
      "Every route charges the standard InstaSwap fee.",
      "You earn 0 bps — the API is free to integrate and free to run.",
      "No payout accounting, no invoices, no thresholds.",
    ],
    activeInfo:
      "Your account is on the default model: your users pay the standard InstaSwap fee on every route and no revenue accrues to you. Pick a model below and request activation when you are ready to monetize your volume.",
  },
  {
    key: "split",
    icon: faScaleBalanced,
    color: "#3ad17e",
    title: "50/50 split",
    tag: null,
    activatable: true,
    points: [
      "The InstaSwap fee on your volume is split 50/50 with you.",
      "The end user pays the same standard fee — your share comes out of ours.",
      "Best for integrations with steady organic volume.",
    ],
    activeInfo:
      "How it works: on every swap your traffic generates, half of the collected InstaSwap fee is credited to you automatically — no setup, no per-route configuration. Track your accrued earnings on the Dashboard and in Swap History; payouts run from your Balances page.",
  },
  {
    key: "custom",
    icon: faSliders,
    color: "#ff4d94",
    title: "Custom fees",
    tag: null,
    activatable: true,
    points: [
      "Your own fee on top of (or instead of) the standard fee, per route or per pair.",
      "Configured by our team to match the terms we agree on.",
      "Best for high-volume or white-label integrations with specific pricing needs.",
    ],
    activeInfo:
      "How it works: your account carries a custom fee schedule agreed with our team — your take is charged on top of (or instead of) the standard fee, per route or per pair, and credited to you on every swap. Want the schedule adjusted? Ping us on Discord.",
  },
  {
    key: "sub",
    icon: faUsers,
    color: "#5aa7ff",
    title: "Sub-affiliates",
    tag: null,
    activatable: true,
    points: [
      "Create sub-affiliate keys and set the fee each one charges yourself.",
      "Track volume and earnings per sub-affiliate in this dashboard.",
      "Best for platforms with multiple products, clients or traffic sources.",
    ],
    activeInfo:
      "How it works: you issue a dedicated X-Sub-Affiliate-Id for each downstream partner or traffic source and set the fee each one charges — that fee is collected on top of the swap and credited to you. Manage everything right here:",
  },
];

const RevenueShare = () => {
  const [current, setCurrent] = useState(null); // 'none' | 'split' | 'custom' | 'sub' | null=loading or unknown
  const [subFeesEnabled, setSubFeesEnabled] = useState(false); // admin toggle: sub-affiliate management available
  const [pending, setPending] = useState(null); // { requestedModel, createdAt } | null
  const [busy, setBusy] = useState(null); // model key while POSTing
  const [err, setErr] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios(`${WALLETLESS_URL}/partner/revenue-model`, {
      headers: { "X-Partner-Hash": localStorage.hash || "" },
    })
      .then((res) => {
        setCurrent(String(res.data?.model || "none").toLowerCase());
        setSubFeesEnabled(res.data?.subFeesEnabled === true);
        setPending(res.data?.pendingRequest || null);
      })
      // Unknown is not "default": a failed lookup must not show the no-revenue-share card as active.
      .catch(() => setErr("Could not load your revenue model right now. Refresh the page or sign in again."))
      .finally(() => setLoaded(true));
  }, []);

  const activeKey = current === null ? null : current === "none" || !current ? "default" : current;

  const requestModel = (model) => {
    setBusy(model);
    setErr(null);
    axios
      .post(
        `${WALLETLESS_URL}/partner/revenue-model-request`,
        { requestedModel: model },
        { headers: { "X-Partner-Hash": localStorage.hash || "" } }
      )
      .then((res) => {
        setPending(
          res.data?.pendingRequest || { requestedModel: model, createdAt: new Date().toISOString() }
        );
      })
      .catch((e) => {
        const msg = e?.response?.data?.error;
        if (e?.response?.status === 409) {
          setPending(e?.response?.data?.pendingRequest || { requestedModel: model, createdAt: new Date().toISOString() });
          setErr("You already have a pending request — we will review it shortly.");
        } else if (msg === "model_already_active") {
          setErr("This model is already active on your account.");
        } else {
          setErr(msg || "Could not submit the request. Please reach out on Discord.");
        }
      })
      .finally(() => setBusy(null));
  };

  return (
    <>
      <Helmet>
        <title>Revenue Share | Instaswap Partners</title>
      </Helmet>

      <div className="secHdr">
        <h1 className="secTitle">
          <FontAwesomeIcon icon={faHandshake} style={{ color: "#3ad17e", marginRight: 12 }} />
          Revenue Share Models
        </h1>
        <p className="secLead">
          New partner accounts start on the no-revenue-share model: the API is free to use and
          every route charges the standard InstaSwap fee. If you bring volume, pick a model
          below and request activation — our team reviews every request, and the fastest way
          to talk terms is Discord.
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {MODELS.map((m) => {
          const isActive = loaded && activeKey === m.key;
          const isPending = pending && String(pending.requestedModel).toLowerCase() === m.key;
          return (
            <div key={m.key} className="panel" style={{ padding: 20, display: "flex", flexDirection: "column", border: isActive ? `1px solid ${m.color}66` : undefined }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span
                  style={{
                    width: 34, height: 34, borderRadius: 10, display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 14, color: m.color,
                    background: `${m.color}1a`, border: `1px solid ${m.color}40`,
                  }}
                >
                  <FontAwesomeIcon icon={m.icon} />
                </span>
                <span style={{ fontSize: 15, fontWeight: 700, color: TEXT }}>{m.title}</span>
                {isActive && (
                  <span className="apBdg new" style={{ marginLeft: "auto" }}>ACTIVE</span>
                )}
                {!isActive && m.tag && (
                  <span className="apBdg soon" style={{ marginLeft: "auto" }}>{m.tag}</span>
                )}
              </div>
              <ul style={{ margin: 0, padding: "0 0 0 2px", listStyle: "none", flex: 1 }}>
                {m.points.map((p, i) => (
                  <li key={i} style={{ display: "flex", gap: 8, fontSize: 13, lineHeight: 1.6, color: BODY, marginBottom: 6 }}>
                    <FontAwesomeIcon icon={faCheck} style={{ color: m.color, marginTop: 4, fontSize: 11 }} />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              {isActive && m.activeInfo && (
                <div
                  style={{
                    marginTop: 12, padding: "10px 12px", borderRadius: 10, fontSize: 12.5,
                    lineHeight: 1.6, color: TEXT, background: `${m.color}14`, border: `1px solid ${m.color}40`,
                  }}
                >
                  {m.activeInfo}
                </div>
              )}
              {m.activatable && !isActive && (
                <button
                  type="button"
                  className="btnPrimary"
                  disabled={!!busy || !!pending}
                  onClick={() => requestModel(m.key)}
                  style={{ marginTop: 14, opacity: pending && !isPending ? 0.5 : 1 }}
                >
                  {isPending ? (
                    <>
                      <FontAwesomeIcon icon={faClock} style={{ marginRight: 7 }} />
                      Requested — pending review
                    </>
                  ) : busy === m.key ? (
                    "Sending…"
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} style={{ marginRight: 7 }} />
                      Request activation
                    </>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {err && (
        <div className="panel" style={{ padding: 14, marginTop: 16, border: "1px solid #b3423a66", color: "#ffb0a8", fontSize: 13 }}>
          {err}
        </div>
      )}

      <div className="panel" style={{ padding: 18, marginTop: 16, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <FontAwesomeIcon icon={faCircleInfo} style={{ color: MUTED }} />
        <span style={{ fontSize: 13, color: BODY, flex: 1, minWidth: 240 }}>
          Requests are reviewed manually. Want to discuss volume, custom pricing or anything
          else first? Ping us on Discord — it is where partner terms actually get done.
        </span>
        <a
          className="btnPrimary"
          href={DISCORD_URL}
          target="_blank"
          rel="noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none" }}
        >
          <FontAwesomeIcon icon={faDiscord} />
          Talk to us on Discord
        </a>
      </div>

      {loaded && (activeKey === "sub" || subFeesEnabled) && (
        <div style={{ marginTop: 26 }}>
          <SubPartnerFees embedded />
        </div>
      )}
    </>
  );
};

export default RevenueShare;
